import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { isMobile } from 'react-device-detect';

// Vite doesn't provide CommonJS require in the browser; eagerly import image URLs instead.
const images = Object.values(
  import.meta.glob('../../assets/promotionalImageDisplay/*.{jpg,jpeg,png}', {
    eager: true,
    import: 'default',
  })
);

const MAX_IMAGES_PER_REFRESH = 5;
const MAX_RANDOM_ATTEMPTS = Math.max(
  1,
  Math.ceil(images.length / MAX_IMAGES_PER_REFRESH)
);

const getRandomSubset = (source, size) => {
  const subset = [];
  const used = new Set();
  const limit = Math.min(size, source.length);
  while (subset.length < limit) {
    const index = Math.floor(Math.random() * source.length);
    if (!used.has(index)) {
      subset.push(source[index]);
      used.add(index);
    }
  }
  return subset;
};

const loadImageSource = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
  });

const PuzzleContainer = styled.div`
  position: relative;
  width: ${isMobile ? '200px' : '400px'};
  height: ${isMobile ? '120px' : '240px'};
  border: 1px solid #ccc;
  margin: 0 auto;
  background-color: #f0f0f0;
  border-radius: 10px;
`;

const PuzzlePieceCanvas = styled.canvas`
  position: absolute;
  cursor: move;
  border: 1px solid #000;
  z-index: 10;
`;

const CaptchaInfo = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
  font-size: 14px;
`;

const TimeRemaining = styled.span`
  margin-right: 10px;
`;

const RefreshText = styled.span`
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #0056b3;
  }
`;

const PuzzleCaptcha = ({ onVerified }) => {
  const canvasRef = useRef(null);
  const pieceCanvasRef = useRef(null);
  const [slotPosition, setSlotPosition] = useState({ x: 0, y: 0 });
  const [piecePosition, setPiecePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [timeLeft, setTimeLeft] = useState(60); // countdown timer of 60 seconds
  const [imagePool, setImagePool] = useState([]);
  const [poolCursor, setPoolCursor] = useState(0);
  const [isSolved, setIsSolved] = useState(false);

  const pieceWidth = isMobile ? 50 : 75;
  const pieceHeight = isMobile ? 50 : 75;

  const resetCaptchaForImage = useCallback((img) => {
    setSelectedImage(img || null);
    setTimeLeft(60);
    setIsSolved(false);
    onVerified?.(false);
  }, [onVerified]);

  const loadImageBatch = useCallback(async (signal) => {
    let attempts = 0;
    let validImages = [];

    while (attempts < MAX_RANDOM_ATTEMPTS && validImages.length === 0) {
      const subset = getRandomSubset(images, MAX_IMAGES_PER_REFRESH);
      const loaded = await Promise.all(subset.map(loadImageSource));
      validImages = loaded.filter((img) => img && img.width > img.height);
      attempts += 1;
    }

    if (signal?.cancelled) {
      return;
    }

    setImagePool(validImages);
    setPoolCursor(0);
    resetCaptchaForImage(validImages[0] || null);
  }, [resetCaptchaForImage]);

  const loadNextImage = useCallback(() => {
    if (imagePool.length === 0 || poolCursor >= imagePool.length - 1) {
      loadImageBatch();
      return;
    }
    const nextIndex = poolCursor + 1;
    setPoolCursor(nextIndex);
    resetCaptchaForImage(imagePool[nextIndex]);
  }, [imagePool, poolCursor, loadImageBatch, resetCaptchaForImage]);

  useEffect(() => {
    const signal = { cancelled: false };
    loadImageBatch(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadImageBatch]);

  useEffect(() => {
    if (!selectedImage) return;

    const canvas = canvasRef.current;
    const pieceCanvas = pieceCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const pieceCtx = pieceCanvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    pieceCtx.clearRect(0, 0, pieceWidth, pieceHeight);

    selectedImage.onload = () => {
      ctx.drawImage(selectedImage, 0, 0, canvas.width, canvas.height);

      const slotX = Math.random() * (canvas.width - pieceWidth);
      const slotY = Math.random() * (canvas.height - pieceHeight);
      setSlotPosition({ x: slotX, y: slotY });

      ctx.save();
      createPuzzlePiece(ctx, slotX, slotY, pieceWidth, pieceHeight);
      ctx.clip();
      const imageData = ctx.getImageData(slotX, slotY, pieceWidth, pieceHeight);
      ctx.clearRect(slotX, slotY, pieceWidth, pieceHeight); 
      ctx.restore();

      pieceCtx.clearRect(0, 0, pieceWidth, pieceHeight);
      pieceCtx.putImageData(imageData, 0, 0);

      setPiecePosition({
        x: Math.random() * (canvas.width - pieceWidth),
        y: Math.random() * (canvas.height - pieceHeight),
      });
    };

    if (selectedImage.complete) {
      selectedImage.onload();
    }
  }, [selectedImage]);

  // Timer effect for expiration
  useEffect(() => {
    if (timeLeft === 0) {
      loadNextImage(); // when time expires, load a new image
      return;
    }

    const timerId = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearTimeout(timerId);
  }, [timeLeft, loadNextImage]);

  const createPuzzlePiece = (ctx, x, y, width, height) => {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + width, y);
    ctx.lineTo(x + width, y + height);
    ctx.lineTo(x, y + height);
    ctx.closePath();
  };

  const handleMouseDown = (e) => {
    const rect = pieceCanvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    if (mouseX >= 0 && mouseX <= pieceWidth && mouseY >= 0 && mouseY <= pieceHeight) {
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const rect = canvasRef.current.getBoundingClientRect();
      const newX = e.clientX - rect.left - pieceWidth / 2;
      const newY = e.clientY - rect.top - pieceHeight / 2;
      setPiecePosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    const dx = piecePosition.x - slotPosition.x;
    const dy = piecePosition.y - slotPosition.y;
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
      setPiecePosition({ x: slotPosition.x, y: slotPosition.y });
      setIsSolved(true);
      onVerified?.(true);
    } else {
      setIsSolved(false);
      onVerified?.(false);
    }
  };

  // Handle touch events for mobile devices
  const handleTouchStart = (e) => {
    const rect = pieceCanvasRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const touchX = touch.clientX - rect.left;
    const touchY = touch.clientY - rect.top;
    if (touchX >= 0 && touchX <= pieceWidth && touchY >= 0 && touchY <= pieceHeight) {
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      const rect = canvasRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      const newX = touch.clientX - rect.left - pieceWidth / 2;
      const newY = touch.clientY - rect.top - pieceHeight / 2;
      setPiecePosition({ x: newX, y: newY });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    const dx = piecePosition.x - slotPosition.x;
    const dy = piecePosition.y - slotPosition.y;
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
      setPiecePosition({ x: slotPosition.x, y: slotPosition.y });
      setIsSolved(true);
      onVerified?.(true);
    } else {
      setIsSolved(false);
      onVerified?.(false);
    }
  };

  return (
    <div>
      <PuzzleContainer>
        <canvas
          ref={canvasRef}
          width={isMobile ? 200 : 400}
          height={isMobile ? 120 : 240}
        />
        <PuzzlePieceCanvas
          ref={pieceCanvasRef}
          width={pieceWidth}
          height={pieceHeight}
          style={{
            top: piecePosition.y,
            left: piecePosition.x,
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />
      </PuzzleContainer>
      <CaptchaInfo>
        <TimeRemaining>Time left: {timeLeft}s</TimeRemaining>
        <RefreshText onClick={loadNextImage}>Refresh Captcha</RefreshText>
      </CaptchaInfo>
    </div>
  );
};

export default PuzzleCaptcha;
